<h2 mat-dialog-title>
  @if (editMode) {
    <svg class="icon-24"><use href="assets/images/ok-icons-sprite.svg#edit-s"></use></svg>
    Rediger prispolitik
  } @else {
    <svg class="icon-24"><use href="assets/images/ok-icons-sprite.svg#plus-s"></use></svg>
    Opret prispolitik
  }
</h2>
<button class="btn-close btn-icon" mat-dialog-close>
  <mat-icon class="material-icons">close</mat-icon>
</button>
<mat-dialog-content>
  <form [formGroup]="pricePolicyFormGroup">
    @if ((data.pricePolicy?.connectorInfo?.connectorCount ?? 0) > 5) {
      <ok-feedback-info>
        <div>
          <strong>Bemærk!</strong><br />
          Da der er {{ data.pricePolicy?.connectorInfo?.connectorCount ?? 0 }} ladepunkter, der bruger denne prispolitik kan en
          prisopdatering tage adskillige sekunder at gemme.
        </div>
      </ok-feedback-info>
    }

    <div class="form-row">
      <mat-form-field title="Navn">
        <mat-label>Navn</mat-label>
        <input matInput formControlName="name" #nameInput />
        @if (pricePolicyFormGroup.controls.name.hasError('required')) {
          <mat-error>Navn er påkrævet</mat-error>
        } @else if (pricePolicyFormGroup.controls.name.hasError('minlength')) {
          <mat-error>Navn skal være mindst 2 tegn</mat-error>
        } @else if (pricePolicyFormGroup.controls.name.hasError('maxlength')) {
          <mat-error>Navn må højst være 50 tegn</mat-error>
        } @else if (pricePolicyFormGroup.controls.name.hasError('pattern')) {
          <mat-error>Navn må ikke kun bestå af mellemrum</mat-error>
        }
      </mat-form-field>
    </div>
    <div class="form-row">
      <h2 class="form-headline">Tilknytning</h2>
      <mat-radio-group formControlName="affiliation" name="affiliation" class="flex-column">
        <mat-radio-button [value]="true">
          <span>Global</span>
        </mat-radio-button>
        <mat-radio-button [value]="false">
          <span>Organisation</span>
        </mat-radio-button>
      </mat-radio-group>
    </div>
    @if (!isGlobalSelected()) {
      <div class="form-row">
        <adm-org-typeahead
          [initialId]="pricePolicyFormGroup.controls.organization.value?.id"
          [resetTrigger]="resetOrgTrigger()"
          (itemSelected)="selectOrganization($event)"
          [disabled]="editMode"
          (clearTypeAhead)="resetOrganization()" />
      </div>
    }

    <adm-admin-enum-select-list
      label="Vælg type"
      [enumObject]="typeOptions()"
      [translatePipe]="translatePricePolicyTypePipe"
      [initialValue]="initialType"
      [disabled]="editMode"
      (enumChanged)="typeSelected($event)" />
    @if (pricePolicyFormGroup.controls.type.touched && pricePolicyFormGroup.controls.type.hasError('required')) {
      <mat-error class="standalone-error">Type er påkrævet</mat-error>
    }

    @if (typeValue() === 'Dynamic') {
      <div class="form-row">
        <adm-price-policy-fallback
          [config]="fallbackConfig()"
          [itemTemplate]="fallbackItemTemplate"
          (itemSelected)="fallbackPricePolicySelected($event)" />
        <p class="helper-text">Hvis ikke elpris kan hentes, vil denne prispolitik blive brugt</p>
        @if (
          pricePolicyFormGroup.controls.fallbackPricePolicy.touched &&
          pricePolicyFormGroup.controls.fallbackPricePolicy.hasError('required')
        ) {
          <mat-error class="standalone-error">Fallback prispolitik er påkrævet</mat-error>
        }
      </div>
      <div class="form-row">
        <adm-price-ex-incl-vat
          headline="Strømsats bund"
          tooltipText="Laveste strømsats pr. kWh, der kan opkræves ved dynamisk prispolitik"
          formControlNameExclVat="lowLimit"
          formControlNameInclVat="lowLimitInclVat"
          [initialExclVat]="initialBasePriceLow"
          [resetTrigger]="typeResetTrigger()" />
        @if (pricePolicyFormGroup.controls.lowLimit.touched && pricePolicyFormGroup.controls.lowLimit.hasError('required')) {
          <mat-error class="standalone-error">Strømsats bund er påkrævet</mat-error>
        } @else if (pricePolicyFormGroup.controls.lowLimit.touched && pricePolicyFormGroup.controls.lowLimit.hasError('max')) {
          <mat-error class="standalone-error">Strømsats bund må ikke overstige 99,99 kr</mat-error>
        } @else if (pricePolicyFormGroup.controls.lowLimit.touched && pricePolicyFormGroup.controls.lowLimit.hasError('min')) {
          <mat-error class="standalone-error">Strømsats bund skal være minimum kr. 0,-</mat-error>
        }
      </div>

      <div class="form-row">
        <adm-price-ex-incl-vat
          headline="Strømsats loft"
          tooltipText="Højeste strømsats pr. kWh, der kan opkræves ved dynamisk prispolitik"
          formControlNameExclVat="highLimit"
          formControlNameInclVat="highLimitInclVat"
          [initialExclVat]="initialBasePriceHigh"
          [resetTrigger]="typeResetTrigger()" />
        @if (pricePolicyFormGroup.controls.highLimit.touched && pricePolicyFormGroup.controls.highLimit.hasError('required')) {
          <mat-error class="standalone-error">Strømsats loft er påkrævet</mat-error>
        } @else if (pricePolicyFormGroup.controls.highLimit.touched && pricePolicyFormGroup.controls.highLimit.hasError('max')) {
          <mat-error class="standalone-error">Strømsats loft må ikke overstige 99,99 kr</mat-error>
        } @else if (pricePolicyFormGroup.controls.highLimit.hasError('lowLimitExceedsHighLimit')) {
          <mat-error class="standalone-error">Strømsats loft skal være større end strømsats bund</mat-error>
        }
      </div>
    }

    @if (typeValue() === 'FixedWithOkMeter' || typeValue() === 'FixedWithoutOkMeter') {
      <div class="form-row">
        <adm-price-ex-incl-vat
          headline="Strømsats"
          formControlNameExclVat="pricePerUnit"
          formControlNameInclVat="pricePerUnitInclVat"
          [initialExclVat]="initialBasePriceExclVat"
          [resetTrigger]="typeResetTrigger()" />
        @if (pricePolicyFormGroup.controls.pricePerUnit.touched && pricePolicyFormGroup.controls.pricePerUnit.hasError('required')) {
          <mat-error class="standalone-error">Strømsats er påkrævet</mat-error>
        } @else if (pricePolicyFormGroup.controls.pricePerUnit.touched && pricePolicyFormGroup.controls.pricePerUnit.hasError('max')) {
          <mat-error class="standalone-error">Strømsats må ikke overstige 99,9 kr</mat-error>
        }
      </div>
    }
    @if (typeValue() === 'FixedWithoutOkMeter' || typeValue() === 'Dynamic') {
      <div class="form-row">
        <adm-price-ex-incl-vat
          headline="OK-tillæg"
          formControlNameExclVat="okSupplement"
          formControlNameInclVat="okSupplementInclVat"
          [initialExclVat]="initialOkSupplementExclVat"
          [resetTrigger]="typeResetTrigger()" />
        @if (pricePolicyFormGroup.controls.okSupplement.touched && pricePolicyFormGroup.controls.okSupplement.hasError('required')) {
          <mat-error class="standalone-error">OK-tillæg er påkrævet</mat-error>
        }
      </div>

      <div class="form-row">
        <adm-price-ex-incl-vat
          headline="Kundetillæg"
          formControlNameExclVat="organizationSupplement"
          formControlNameInclVat="organizationSupplementInclVat"
          [initialExclVat]="initialCustomerSupplementExclVat"
          [resetTrigger]="typeResetTrigger()" />
        @if (
          pricePolicyFormGroup.controls.organizationSupplement.touched &&
          pricePolicyFormGroup.controls.organizationSupplement.hasError('required')
        ) {
          <mat-error class="standalone-error">Kundetillæg er påkrævet</mat-error>
        }
      </div>
    }
  </form>

  <adm-price-feedback [config]="priceFeedbackConfig()" />
</mat-dialog-content>
<mat-dialog-actions>
  <div class="unit-identifier">
    @if (data.pricePolicy) {
      <span>Prispolitik:</span>
    }
    {{ data.pricePolicy?.name }}
  </div>

  <button mat-button (click)="cancel()">Annuller</button>
  <button
    mat-flat-button
    color="primary"
    (click)="editMode ? submitUpdate() : submitCreate()"
    class="add-button"
    [class.spinner]="saving()"
    [disabled]="saving() || pricePolicyFormGroup.invalid || !pricePolicyFormGroup.dirty">
    {{ editMode ? 'Gem' : 'Opret' }}
  </button>
</mat-dialog-actions>

<ng-template #fallbackItemTemplate let-item>
  <div class="fallback-item">
    <div>{{ item.name }}</div>
    <div class="fallback-item-price">
      {{ item.basePriceInKrWithoutVat?.fixed?.value | dkCurrency }}
    </div>
  </div>
</ng-template>
